import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Mail, MessageSquare, Phone, Plus, Edit2, Trash2, Copy } from 'lucide-react';
import { supabase } from '@/lib/supabase';

type TemplateType = 'email' | 'sms' | 'ai_call';

interface Template {
  id: string;
  name: string;
  type: TemplateType;
  content: string;
  subject?: string;
  created_at: string;
}

export default function TemplatesPage() {
  const [activeTab, setActiveTab] = useState<TemplateType>('email');
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      
      // Load email templates
      const { data: emailTemplates, error: emailError } = await supabase
        .from('email_templates')
        .select('id, name, subject, body, created_at')
        .order('created_at', { ascending: false });

      // Load SMS templates
      const { data: smsTemplates, error: smsError } = await supabase
        .from('sms_templates')
        .select('id, name, message, created_at')
        .order('created_at', { ascending: false });

      if (emailError) console.error('Error loading email templates:', emailError);
      if (smsError) console.error('Error loading SMS templates:', smsError);

      // Combine into unified format
      const allTemplates: Template[] = [
        ...(emailTemplates || []).map(t => ({
          id: t.id,
          name: t.name,
          type: 'email' as TemplateType,
          subject: t.subject,
          content: t.body,
          created_at: t.created_at,
        })),
        ...(smsTemplates || []).map(t => ({
          id: t.id,
          name: t.name,
          type: 'sms' as TemplateType,
          content: t.message,
          created_at: t.created_at,
        })),
      ];

      setTemplates(allTemplates);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const filteredTemplates = templates.filter(t => t.type === activeTab);

  const handleDelete = (id: string) => {
    if (!confirm('Delete this template?')) return;
    setTemplates(templates.filter(t => t.id !== id));
    toast.success('Template deleted');
  };

  const handleDuplicate = (template: Template) => {
    const newTemplate = { ...template, id: Date.now().toString(), name: `${template.name} (Copy)` };
    setTemplates([newTemplate, ...templates]);
    toast.success('Template duplicated');
  };

  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Template Management</h1>
            <p className="text-gray-600 mt-2">Manage email, SMS, and AI call templates</p>
          </div>
          <button onClick={() => { setEditingTemplate(null); setShowModal(true); }}
            className="btn-primary flex items-center gap-2">
            <Plus size={20} /> Create Template
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b">
        <button onClick={() => setActiveTab('email')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'email' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}>
          <Mail className="inline mr-2" size={18} /> Email Templates
        </button>
        <button onClick={() => setActiveTab('sms')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'sms' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}>
          <MessageSquare className="inline mr-2" size={18} /> SMS Templates
        </button>
        <button onClick={() => setActiveTab('ai_call')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeTab === 'ai_call' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}>
          <Phone className="inline mr-2" size={18} /> AI Call Scripts
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="card">
          <p className="text-sm font-medium text-gray-600">Email Templates</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{templates.filter(t => t.type === 'email').length}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600">SMS Templates</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{templates.filter(t => t.type === 'sms').length}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-600">AI Call Scripts</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{templates.filter(t => t.type === 'ai_call').length}</p>
        </div>
      </div>

      {/* Template List */}
      {filteredTemplates.length === 0 ? (
        <div className="card text-center py-12">
          {activeTab === 'email' && <Mail className="mx-auto text-gray-400 mb-4" size={48} />}
          {activeTab === 'sms' && <MessageSquare className="mx-auto text-gray-400 mb-4" size={48} />}
          {activeTab === 'ai_call' && <Phone className="mx-auto text-gray-400 mb-4" size={48} />}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No templates yet</h3>
          <p className="text-gray-600 mb-4">Create your first {activeTab.replace('_', ' ')} template</p>
          <button onClick={() => setShowModal(true)} className="btn-primary">
            <Plus size={20} className="inline mr-2" /> Create Template
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {(filteredTemplates || []).map((template) => (
            <div key={template.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{template.name}</h3>
                  {template.subject && (
                    <p className="text-sm text-gray-600 mb-2"><strong>Subject:</strong> {template.subject}</p>
                  )}
                  <p className="text-gray-600 mb-3 line-clamp-2">{template.content}</p>
                  <p className="text-xs text-gray-500">Created {new Date(template.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={() => handleDuplicate(template)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded" title="Duplicate">
                    <Copy size={18} />
                  </button>
                  <button onClick={() => { setEditingTemplate(template); setShowModal(true); }}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded" title="Edit">
                    <Edit2 size={18} />
                  </button>
                  <button onClick={() => handleDelete(template.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editingTemplate}
          type={activeTab}
          onClose={() => { setShowModal(false); setEditingTemplate(null); }}
          onSave={(newTemplate) => {
            if (editingTemplate) {
              setTemplates(templates.map(t => t.id === editingTemplate.id ? newTemplate : t));
              toast.success('Template updated');
            } else {
              setTemplates([newTemplate, ...templates]);
              toast.success('Template created');
            }
            setShowModal(false);
            setEditingTemplate(null);
          }}
        />
      )}
    </div>
  );
}

function TemplateModal({ template, type, onClose, onSave }: {
  template: Template | null;
  type: TemplateType;
  onClose: () => void;
  onSave: (template: Template) => void;
}) {
  const [name, setName] = useState(template?.name || '');
  const [subject, setSubject] = useState(template?.subject || '');
  const [content, setContent] = useState(template?.content || '');

  const handleSave = () => {
    if (!name.trim() || !content.trim()) {
      toast.error('Name and content are required');
      return;
    }
    const newTemplate: Template = {
      id: template?.id || Date.now().toString(),
      name: name.trim(),
      type,
      content: content.trim(),
      subject: type === 'email' ? subject.trim() : undefined,
      created_at: template?.created_at || new Date().toISOString(),
    };
    onSave(newTemplate);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {template ? 'Edit' : 'Create'} {type.replace('_', ' ').toUpperCase()} Template
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Template Name *</label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Welcome Email" className="input w-full" />
            </div>
            {type === 'email' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Line *</label>
                <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., Welcome to Our Service!" className="input w-full" />
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {type === 'email' ? 'Email Body' : type === 'sms' ? 'SMS Message' : 'Call Script'} *
              </label>
              <textarea value={content} onChange={(e) => setContent(e.target.value)}
                placeholder="Enter template content... Use {name}, {company}, etc. for variables"
                rows={type === 'sms' ? 4 : 8} className="input w-full" />
              {type === 'sms' && (
                <p className="text-xs text-gray-500 mt-1">{content.length} / 160 characters</p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 pt-6 border-t mt-6">
            <button onClick={onClose} className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
            <button onClick={handleSave} className="btn-primary">
              {template ? 'Update' : 'Create'} Template
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
